import { FormStructure, FormTheme } from '@/features/application/types/forms';
import { US_STATES, COUNTRIES } from './address-options';
import { COMPANY_TYPES } from './company-types';

// Default theme for new form mappings
export const defaultTheme: FormTheme = {
  primary: '#0F172A', // Slate-900 for primary background
  accent: '#FFFFFF', // White for accents
  background: '#f2eeee', // Light gray for content background
  formBackground: '#7e7ea0', // Dark blue-gray for form background
};

// Initial form structure template for new field mappings
export const initialFormStructure: FormStructure = {
  logo: null,
  pages: [
    {
      id: 'business-info',
      title: 'Business Information',
      sections: [
        {
          id: 'business-basics',
          title: 'Basic Business Information',
          columns: 2,
          fields: [
            {
              id: 'companyName',
              type: 'text',
              label: 'Company Name',
              value: '',
              placeholder: 'Test Company Inc.',
              required: true,
              displayName: 'Company Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'dba',
              type: 'text',
              label: 'DBA (Doing Business As)',
              value: '',
              placeholder: 'Business Trading Name',
              required: false,
              displayName: 'DBA (Doing Business As)',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'federalTaxId',
              type: 'text',
              label: 'Federal Tax ID',
              value: '',
              placeholder: '12-3456789',
              required: true,
              displayName: 'Federal Tax ID',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'companyType',
              type: 'picklist',
              label: 'Company Type',
              value: '',
              placeholder: 'Select company type...',
              required: true,
              displayName: 'Company Type',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              originalPicklistOptions: COMPANY_TYPES,
              currentPicklistOptions: COMPANY_TYPES,
              path: [],
            },
            {
              id: 'industryDescription',
              type: 'text',
              label: 'Industry Description',
              value: '',
              placeholder: 'Software Development',
              required: true,
              displayName: 'Industry Description',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'businessPhone',
              type: 'phone',
              label: 'Business Phone',
              value: '',
              placeholder: '555-123-4567',
              required: true,
              displayName: 'Business Phone',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 12,
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'fax',
              type: 'phone',
              label: 'Fax',
              value: '',
              placeholder: '555-765-4321',
              required: false,
              displayName: 'Fax',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'businessStartDate',
              type: 'date',
              label: 'Business Start Date',
              value: '',
              placeholder: '2020-01-15',
              required: true,
              displayName: 'Business Start Date',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
          ],
        },
        {
          id: 'business-address',
          title: 'Business Address',
          columns: 2,
          fields: [
            {
              id: 'businessAddress',
              type: 'text',
              label: 'Street Address',
              value: '',
              placeholder: '123 Main Street',
              required: true,
              displayName: 'Street Address',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'businessCity',
              type: 'text',
              label: 'City',
              value: '',
              placeholder: 'San Francisco',
              required: true,
              displayName: 'City',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'businessState',
              type: 'picklist',
              label: 'State',
              value: '',
              placeholder: 'Select a state...',
              required: true,
              displayName: 'State',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              originalPicklistOptions: US_STATES,
              currentPicklistOptions: US_STATES,
              path: [],
            },
            {
              id: 'businessZip',
              type: 'text',
              label: 'ZIP Code',
              value: '',
              placeholder: '94105',
              required: true,
              displayName: 'ZIP Code',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 5,
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'businessCountry',
              type: 'picklist',
              label: 'Country',
              value: '',
              placeholder: 'Select a country...',
              required: true,
              displayName: 'Country',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              originalPicklistOptions: COUNTRIES,
              currentPicklistOptions: COUNTRIES,
              path: [],
            },
          ],
        },
      ],
    },
    {
      id: 'contact-info',
      title: 'Contact Information',
      sections: [
        {
          id: 'primary-owner-info',
          title: 'Contact Information',
          columns: 2,
          fields: [
            {
              id: 'owner1FirstName',
              type: 'text',
              label: 'Primary Owner First Name',
              value: '',
              placeholder: 'John',
              required: true,
              displayName: 'First Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1LastName',
              type: 'text',
              label: 'Primary Owner Last Name',
              value: '',
              placeholder: 'Smith',
              required: true,
              displayName: 'Last Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1Email',
              type: 'email',
              label: 'Primary Owner Email',
              value: '',
              placeholder: 'john.smith@example.com',
              required: true,
              displayName: 'Email',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1Phone',
              type: 'phone',
              label: 'Primary Owner Phone',
              value: '',
              placeholder: '555-123-4567',
              required: true,
              displayName: 'Phone',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1OwnershipPercentage',
              type: 'number',
              label: 'Primary Owner Ownership %',
              value: '',
              placeholder: '60',
              required: true,
              displayName: 'Ownership %',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 3,
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1SSN',
              type: 'number',
              label: 'Primary Owner Social Security Number',
              value: '',
              placeholder: 123456789,
              required: true,
              displayName: 'SSN',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 9,
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1DOB',
              type: 'date',
              label: 'Primary Owner Date of Birth',
              value: '',
              placeholder: '1980-01-01',
              required: true,
              displayName: 'Date of Birth',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
          ],
        },
        {
          id: 'primary-owner-address',
          title: 'Primary Owner Address',
          columns: 2,
          fields: [
            {
              id: 'owner1Address',
              type: 'text',
              label: 'Primary Owner Street Address',
              value: '',
              placeholder: '123 Main Street',
              required: true,
              displayName: 'Street Address',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1City',
              type: 'text',
              label: 'Primary Owner City',
              value: '',
              placeholder: 'San Francisco',
              required: true,
              displayName: 'City',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1State',
              type: 'picklist',
              label: 'Primary Owner State',
              value: '',
              placeholder: 'Select a state...',
              required: true,
              displayName: 'State',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              originalPicklistOptions: US_STATES,
              currentPicklistOptions: US_STATES,
              path: [],
            },
            {
              id: 'owner1Zip',
              type: 'text',
              label: 'Primary Owner ZIP Code',
              value: '',
              placeholder: '94105',
              required: true,
              displayName: 'ZIP Code',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 5,
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1Country',
              type: 'picklist',
              label: 'Primary Owner Country',
              value: '',
              placeholder: 'Select a country...',
              required: true,
              displayName: 'Country',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              originalPicklistOptions: COUNTRIES,
              currentPicklistOptions: COUNTRIES,
              path: [],
            },
          ],
        },
        {
          id: 'secondary-owner-info',
          title: 'Secondary Owner Information',
          columns: 2,
          fields: [
            {
              id: 'owner2FirstName',
              type: 'text',
              label: 'Second Owner First Name',
              value: '',
              placeholder: 'Jane',
              required: true,
              displayName: 'First Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2LastName',
              type: 'text',
              label: 'Second Owner Last Name',
              value: '',
              placeholder: 'Doe',
              required: true,
              displayName: 'Last Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2Email',
              type: 'email',
              label: 'Second Owner Email',
              value: '',
              placeholder: 'jane.doe@example.com',
              required: true,
              displayName: 'Email',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2Phone',
              type: 'phone',
              label: 'Second Owner Phone',
              value: '',
              placeholder: '555-987-6543',
              required: true,
              displayName: 'Phone',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2OwnershipPercentage',
              type: 'number',
              label: 'Second Owner Ownership %',
              value: '',
              placeholder: '20',
              required: true,
              displayName: 'Ownership %',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 3,
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2SSN',
              type: 'number',
              label: 'Second Owner Social Security Number',
              value: '',
              placeholder: 987654321,
              required: true,
              displayName: 'SSN',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 9,
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2DOB',
              type: 'date',
              label: 'Second Owner Date of Birth',
              value: '',
              placeholder: '1985-01-01',
              required: true,
              displayName: 'Date of Birth',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
          ],
        },
        {
          id: 'secondary-owner-address',
          title: 'Secondary Owner Address',
          columns: 2,
          fields: [
            {
              id: 'owner2Address',
              type: 'text',
              label: 'Second Owner Street Address',
              value: '',
              placeholder: '123 Main Street',
              required: true,
              displayName: 'Street Address',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2City',
              type: 'text',
              label: 'Second Owner City',
              value: '',
              placeholder: 'San Francisco',
              required: true,
              displayName: 'City',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2State',
              type: 'picklist',
              label: 'Second Owner State',
              value: '',
              placeholder: 'Select a state...',
              required: true,
              displayName: 'State',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              originalPicklistOptions: US_STATES,
              currentPicklistOptions: US_STATES,
              path: [],
            },
            {
              id: 'owner2Zip',
              type: 'text',
              label: 'Second Owner ZIP Code',
              value: '',
              placeholder: '94105',
              required: true,
              displayName: 'ZIP Code',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 5,
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2Country',
              type: 'picklist',
              label: 'Second Owner Country',
              value: '',
              placeholder: 'Select a country...',
              required: true,
              displayName: 'Country',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              originalPicklistOptions: COUNTRIES,
              currentPicklistOptions: COUNTRIES,
              path: [],
            },
          ],
        },
        {
          id: 'tertiary-owner-info',
          title: 'Third Owner Information',
          columns: 2,
          fields: [
            {
              id: 'owner3FirstName',
              type: 'text',
              label: 'Third Owner First Name',
              value: '',
              placeholder: 'Bob',
              required: true,
              displayName: 'First Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3LastName',
              type: 'text',
              label: 'Third Owner Last Name',
              value: '',
              placeholder: 'Johnson',
              required: true,
              displayName: 'Last Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3Email',
              type: 'email',
              label: 'Third Owner Email',
              value: '',
              placeholder: 'bob.johnson@example.com',
              required: true,
              displayName: 'Email',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3Phone',
              type: 'phone',
              label: 'Third Owner Phone',
              value: '',
              placeholder: '555-234-5678',
              required: true,
              displayName: 'Phone',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3OwnershipPercentage',
              type: 'number',
              label: 'Third Owner Ownership %',
              value: '',
              placeholder: '20',
              required: true,
              displayName: 'Ownership %',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 3,
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3SSN',
              type: 'number',
              label: 'Third Owner Social Security Number',
              value: '',
              placeholder: 123456789,
              required: true,
              displayName: 'SSN',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 9,
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3DOB',
              type: 'date',
              label: 'Third Owner Date of Birth',
              value: '',
              placeholder: '2000-01-01',
              required: true,
              displayName: 'Date of Birth',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
          ],
        },
        {
          id: 'tertiary-owner-address',
          title: 'Third Owner Address',
          columns: 2,
          fields: [
            {
              id: 'owner3Address',
              type: 'text',
              label: 'Third Owner Street Address',
              value: '',
              placeholder: '123 Main Street',
              required: true,
              displayName: 'Street Address',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3City',
              type: 'text',
              label: 'Third Owner City',
              value: '',
              placeholder: 'San Francisco',
              required: true,
              displayName: 'City',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3State',
              type: 'picklist',
              label: 'Third Owner State',
              value: '',
              placeholder: 'Select a state...',
              required: true,
              displayName: 'State',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              originalPicklistOptions: US_STATES,
              currentPicklistOptions: US_STATES,
              path: [],
            },
            {
              id: 'owner3Zip',
              type: 'text',
              label: 'Third Owner ZIP Code',
              value: '',
              placeholder: '94105',
              required: true,
              displayName: 'ZIP Code',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 5,
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3Country',
              type: 'picklist',
              label: 'Third Owner Country',
              value: '',
              placeholder: 'Select a country...',
              required: true,
              displayName: 'Country',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              originalPicklistOptions: COUNTRIES,
              currentPicklistOptions: COUNTRIES,
              path: [],
            },
          ],
        },
      ],
    },
    {
      id: 'underwriting',
      title: 'Underwriting Information',
      sections: [
        {
          id: 'loan-details',
          title: 'Loan Details',
          columns: 2,
          fields: [
            {
              id: 'loanAmount',
              type: 'number',
              label: 'Requested Loan Amount',
              value: '',
              placeholder: '50000',
              required: true,
              displayName: 'Requested Loan Amount',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'loanPurpose',
              type: 'text',
              label: 'Loan Purpose',
              value: '',
              placeholder: 'Business Expansion',
              required: true,
              displayName: 'Loan Purpose',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'annualRevenue',
              type: 'number',
              label: 'Annual Revenue',
              value: '',
              placeholder: '500000',
              required: true,
              displayName: 'Annual Revenue',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
          ],
        },
        {
          id: 'final-requirements',
          title: 'Required Documents & Signatures',
          columns: 1,
          fields: [
            {
              id: 'bank-statements',
              type: 'file',
              label: 'Upload Bank Statements (last 4 months)',
              value: null,
              required: false,
              displayName: 'Upload Bank Statements',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              acceptedFileTypes: ['.pdf', '.jpg', '.png'],
              path: [],
            },
            {
              id: 'signature1',
              type: 'signature',
              label: 'Primary Signer Signature',
              value: '',
              required: true,
              displayName: 'Primary Signer Signature',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'signature2',
              type: 'signature',
              label: 'Second Signer Signature',
              value: '',
              required: true,
              displayName: 'Second Signer Signature',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'signature3',
              type: 'signature',
              label: 'Third Signer Signature',
              value: '',
              required: true,
              displayName: 'Third Signer Signature',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
          ],
        },
      ],
    },
    {
      id: 'bank-statement-analysis',
      title: 'Bank Statement Analysis',
      sections: [
        {
          id: 'analysis-results',
          title: 'Analysis Results',
          columns: 2,
          fields: [
            // Balance Information
            {
              id: 'averageBalance',
              type: 'number',
              label: 'Average Ending Balance',
              value: '',
              required: false,
              displayName: 'Average Ending Balance',
              description:
                'Average balance at the end of each statement. If an applicant uploads multiple bank statements, we will average the ending balances for each statement.',
              placeholder: 10000,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'negativeBalanceDays',
              type: 'number',
              label: 'Average Number of Negative Days',
              value: '',
              required: false,
              displayName: 'Average Number of Negative Days',
              description:
                'Number of days with negative balance. If an applicant uploads multiple bank statements, we will average the number of negative days across all statements.',
              placeholder: 3,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'withdrawalCount',
              type: 'number',
              label: 'Average Number of Withdrawals',
              value: '',
              required: false,
              displayName: 'Average Number of Withdrawals',
              description:
                'Total count of withdrawal transactions. If an applicant uploads multiple bank statements, we will average the number of withdrawals across all statements.',
              placeholder: 12,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'depositCount',
              type: 'number',
              label: 'Average Number of Deposits',
              value: '',
              required: false,
              displayName: 'Average Number of Deposits',
              description:
                'Total count of deposit transactions. If an applicant uploads multiple bank statements, we will average the number of deposits across all statements.',
              placeholder: 15,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'totalWithdrawals',
              type: 'number',
              label: 'Average Withdrawal Amount',
              value: '',
              required: false,
              displayName: 'Average Withdrawal Amount',
              description:
                'Sum of all withdrawal transactions. If an applicant uploads multiple bank statements, we will average the total withdrawal amounts across all statements.',
              placeholder: 4500,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'totalDeposits',
              type: 'number',
              label: 'Average Deposit Amount',
              value: '',
              required: false,
              displayName: 'Average Deposit Amount',
              description:
                'Sum of all deposit transactions. If an applicant uploads multiple bank statements, we will average the total deposit amounts across all statements.',
              placeholder: 5000,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            // --- Competitor Position Analysis ---
            {
              id: 'totalPositions',
              type: 'number',
              label: 'Total Competitor Positions Detected',
              value: '',
              required: false,
              displayName: 'Total Competitor Positions Detected',
              description:
                'The total number of distinct competitors detected making regular debits (daily/weekly).',
              placeholder: 2,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            // Competitor 1
            {
              id: 'competitorName1',
              type: 'text',
              label: 'Competitor 1 Name',
              value: '',
              required: false,
              displayName: 'Competitor 1 Name',
              description: 'Name of the first detected competitor based on transaction analysis.',
              placeholder: 'Competitor 1',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'competitorAmount1',
              type: 'number',
              label: 'Competitor 1 Payment Amount',
              value: '',
              required: false,
              displayName: 'Competitor 1 Payment Amount',
              description: 'Modal payment amount detected for the first competitor.',
              placeholder: 500,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'competitorFrequency1',
              type: 'text',
              label: 'Competitor 1 Payment Frequency',
              value: '',
              required: false,
              displayName: 'Competitor 1 Payment Frequency',
              description: 'Detected payment frequency (daily/weekly) for the first competitor.',
              placeholder: 'Daily/Weekly',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            // Competitor 2
            {
              id: 'competitorName2',
              type: 'text',
              label: 'Competitor 2 Name',
              value: '',
              required: false,
              displayName: 'Competitor 2 Name',
              description: 'Name of the second detected competitor.',
              placeholder: 'Competitor 2',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'competitorAmount2',
              type: 'number',
              label: 'Competitor 2 Payment Amount',
              value: '',
              required: false,
              displayName: 'Competitor 2 Payment Amount',
              description: 'Modal payment amount detected for the second competitor.',
              placeholder: 250,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'competitorFrequency2',
              type: 'text',
              label: 'Competitor 2 Payment Frequency',
              value: '',
              required: false,
              displayName: 'Competitor 2 Payment Frequency',
              description: 'Detected payment frequency (daily/weekly) for the second competitor.',
              placeholder: 'Daily/Weekly',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            // Competitor 3
            {
              id: 'competitorName3',
              type: 'text',
              label: 'Competitor 3 Name',
              value: '',
              required: false,
              displayName: 'Competitor 3 Name',
              description: 'Name of the third detected competitor.',
              placeholder: 'Competitor 3',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'competitorAmount3',
              type: 'number',
              label: 'Competitor 3 Payment Amount',
              value: '',
              required: false,
              displayName: 'Competitor 3 Payment Amount',
              description: 'Modal payment amount detected for the third competitor.',
              placeholder: 100,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'competitorFrequency3',
              type: 'text',
              label: 'Competitor 3 Payment Frequency',
              value: '',
              required: false,
              displayName: 'Competitor 3 Payment Frequency',
              description: 'Detected payment frequency (daily/weekly) for the third competitor.',
              placeholder: 'Daily/Weekly',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            // Competitor 4
            {
              id: 'competitorName4',
              type: 'text',
              label: 'Competitor 4 Name',
              value: '',
              required: false,
              displayName: 'Competitor 4 Name',
              description: 'Name of the fourth detected competitor.',
              placeholder: 'Competitor 4',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'competitorAmount4',
              type: 'number',
              label: 'Competitor 4 Payment Amount',
              value: '',
              required: false,
              displayName: 'Competitor 4 Payment Amount',
              description: 'Modal payment amount detected for the fourth competitor.',
              placeholder: 100,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'competitorFrequency4',
              type: 'text',
              label: 'Competitor 4 Payment Frequency',
              value: '',
              required: false,
              displayName: 'Competitor 4 Payment Frequency',
              description: 'Detected payment frequency (daily/weekly) for the fourth competitor.',
              placeholder: 'Daily/Weekly',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            // Competitor 5
            {
              id: 'competitorName5',
              type: 'text',
              label: 'Competitor 5 Name',
              value: '',
              required: false,
              displayName: 'Competitor 5 Name',
              description: 'Name of the fifth detected competitor.',
              placeholder: 'Competitor 5',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'competitorAmount5',
              type: 'number',
              label: 'Competitor 5 Payment Amount',
              value: '',
              required: false,
              displayName: 'Competitor 5 Payment Amount',
              description: 'Modal payment amount detected for the fifth competitor.',
              placeholder: 100,
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
            {
              id: 'competitorFrequency5',
              type: 'text',
              label: 'Competitor 5 Payment Frequency',
              value: '',
              required: false,
              displayName: 'Competitor 5 Payment Frequency',
              description: 'Detected payment frequency (daily/weekly) for the fifth competitor.',
              placeholder: 'Daily/Weekly',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
          ],
        },
      ],
    },
  ],
  theme: defaultTheme,
};
